import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card";

export type KpiTone = "default" | "warning" | "danger";

interface KpiTileProps {
  label: string;
  icon?: ReactNode;
  tone?: KpiTone;
  /** The headline value. Ignored while `loading`. */
  value?: ReactNode;
  /** Secondary line under the value. */
  sub?: ReactNode;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  /** Smaller value type for long values (e.g. dates). */
  compactValue?: boolean;
}

/**
 * One dashboard status/metric tile. Shows a skeleton while loading and a compact
 * retry affordance on error, so a single failed query never breaks the page.
 */
const KpiTile: FC<KpiTileProps> = ({
  label,
  icon,
  tone = "default",
  value,
  sub,
  loading = false,
  error = false,
  onRetry,
  compactValue = false,
}) => {
  const { t } = useTranslation();

  return (
    <Card customClass="kpi-tile">
      <div className="kpi-tile__top">
        <span className="kpi-tile__label">{label}</span>
        {icon && (
          <span className={`kpi-tile__icon kpi-tile__icon--${tone}`}>{icon}</span>
        )}
      </div>

      {loading && (
        <>
          <span className="kpi-tile__skeleton kpi-tile__skeleton--value" />
          <span className="kpi-tile__skeleton kpi-tile__skeleton--sub" />
        </>
      )}

      {!loading && error && (
        <div className="kpi-tile__error">
          <span>{t("dashboard.tileError")}</span>
          {onRetry && (
            <button type="button" onClick={onRetry} className="kpi-tile__retry">
              {t("retry")}
            </button>
          )}
        </div>
      )}

      {!loading && !error && (
        <>
          <div
            className={`kpi-tile__value${
              compactValue ? " kpi-tile__value--compact" : ""
            }`}
          >
            {value}
          </div>
          {sub && <div className="kpi-tile__sub">{sub}</div>}
        </>
      )}
    </Card>
  );
};

export default KpiTile;
